import React, { useState, useEffect } from 'react';
import db from '../services/database';
import './EntityManager.css';

function EntityManager() {
    const [selectedTable, setSelectedTable] = useState('Materials');
    const [tableData, setTableData] = useState([]);
    const [columns, setColumns] = useState([]);
    const [editingRow, setEditingRow] = useState(null);
    const [isAdding, setIsAdding] = useState(false);
    const [formData, setFormData] = useState({});
    const [message, setMessage] = useState({ type: '', text: '' });
    const [foreignKeys, setForeignKeys] = useState({});

    const tables = [
        { name: 'Materials', label: 'Матеріали', canEdit: true, primaryKey: 'id' },
        { name: 'MaterialTypes', label: 'Типи матеріалів', canEdit: true, primaryKey: 'id' },
        { name: 'Elements', label: 'Елементи', canEdit: true, primaryKey: 'id' },
        { name: 'MaterialComposition', label: 'Склад матеріалів', canEdit: true, primaryKey: ['material_id', 'element_id'] },
        { name: 'Properties', label: 'Властивості', canEdit: true, primaryKey: 'id' },
        { name: 'MaterialProperties', label: 'Властивості матеріалів', canEdit: true, primaryKey: ['material_id', 'property_id'] },
        { name: 'Applications', label: 'Застосування', canEdit: true, primaryKey: 'id' },
        { name: 'MaterialApplications', label: 'Застосування матеріалів', canEdit: true, primaryKey: ['material_id', 'application_id'] }
    ];

    useEffect(() => {
        loadTableData(selectedTable);
        loadForeignKeys(selectedTable);
    }, [selectedTable]);

    const loadForeignKeys = (tableName) => {
        const fkData = {};
        
        try {
            if (tableName === 'Materials') {
                const types = db.executeQuery('SELECT id, name FROM MaterialTypes');
                fkData.type_id = types;
            } else if (tableName === 'MaterialComposition') {
                const materials = db.executeQuery('SELECT id, name FROM Materials');
                const elements = db.executeQuery('SELECT id, symbol, name FROM Elements');
                fkData.material_id = materials;
                fkData.element_id = elements;
            } else if (tableName === 'MaterialProperties') {
                const materials = db.executeQuery('SELECT id, name FROM Materials');
                const properties = db.executeQuery('SELECT id, name FROM Properties');
                fkData.material_id = materials;
                fkData.property_id = properties;
            } else if (tableName === 'MaterialApplications') {
                const materials = db.executeQuery('SELECT id, name FROM Materials');
                const applications = db.executeQuery('SELECT id, name FROM Applications');
                fkData.material_id = materials;
                fkData.application_id = applications;
            }
            
            setForeignKeys(fkData);
        } catch (error) {
            console.error('Error loading foreign keys:', error);
        }
    };

    const loadTableData = (tableName) => {
        try {
            const data = db.executeQuery(`SELECT * FROM ${tableName}`);
            setTableData(data);
            
            if (data.length > 0) {
                setColumns(Object.keys(data[0]));
            } else {
                const schemaQuery = `PRAGMA table_info(${tableName})`;
                const schema = db.executeQuery(schemaQuery);
                setColumns(schema.map(col => col.name));
            }
            
            setMessage({ type: '', text: '' });
        } catch (error) {
            console.error('Error loading table data:', error);
            setTableData([]);
            setColumns([]);
        }
    };

    const handleTableChange = (tableName) => {
        setSelectedTable(tableName);
        setEditingRow(null);
        setIsAdding(false);
        setFormData({});
    };

    const startAdd = () => {
        const currentTable = tables.find(t => t.name === selectedTable);
        const primaryKey = currentTable.primaryKey;
        const isCompositePK = Array.isArray(primaryKey);
        
        const newFormData = {};
        columns.forEach(col => {
            // For composite PK tables, include all fields
            // For single PK tables, exclude 'id' (auto-increment)
            if (isCompositePK || col !== primaryKey) {
                newFormData[col] = '';
            }
        });
        setFormData(newFormData);
        setIsAdding(true);
        setEditingRow(null);
    };

    const startEdit = (row) => {
        setFormData({ ...row });
        setEditingRow(row);
        setIsAdding(false);
    };

    const cancelEdit = () => {
        setEditingRow(null);
        setIsAdding(false);
        setFormData({});
    };

    const handleInputChange = (column, value) => {
        setFormData(prev => ({
            ...prev,
            [column]: value
        }));
    };

    const saveRecord = () => {
        try {
            const currentTable = tables.find(t => t.name === selectedTable);
            const primaryKey = currentTable.primaryKey;
            const isCompositePK = Array.isArray(primaryKey);
            
            if (isAdding) {
                // Insert new record
                const cols = Object.keys(formData).filter(col => {
                    if (isCompositePK) {
                        return formData[col] !== '';
                    }
                    return col !== 'id' && formData[col] !== '';
                });
                const values = cols.map(col => formData[col]);
                const placeholders = cols.map(() => '?').join(', ');
                
                const query = `INSERT INTO ${selectedTable} (${cols.join(', ')}) VALUES (${placeholders})`;
                db.db.run(query, values);
                
                setMessage({ type: 'success', text: 'Запис успішно додано!' });
            } else {
                // Update existing record
                let whereClause;
                let whereValues;
                let updateCols;
                
                if (isCompositePK) {
                    // Composite primary key
                    updateCols = Object.keys(formData).filter(col => !primaryKey.includes(col));
                    whereClause = primaryKey.map(key => `${key} = ?`).join(' AND ');
                    whereValues = primaryKey.map(key => formData[key]);
                } else {
                    // Single primary key
                    updateCols = Object.keys(formData).filter(col => col !== primaryKey);
                    whereClause = `${primaryKey} = ?`;
                    whereValues = [formData[primaryKey]];
                }
                
                const setClause = updateCols.map(col => `${col} = ?`).join(', ');
                const values = [...updateCols.map(col => formData[col]), ...whereValues];
                
                const query = `UPDATE ${selectedTable} SET ${setClause} WHERE ${whereClause}`;
                db.db.run(query, values);
                
                setMessage({ type: 'success', text: 'Запис успішно оновлено!' });
            }
            
            loadTableData(selectedTable);
            cancelEdit();
            
            setTimeout(() => setMessage({ type: '', text: '' }), 3000);
        } catch (error) {
            console.error('Error saving record:', error);
            setMessage({ type: 'error', text: 'Помилка збереження: ' + error.message });
        }
    };

    const deleteRecord = (row) => {
        if (!window.confirm('Ви впевнені, що хочете видалити цей запис?')) {
            return;
        }
        
        try {
            const currentTable = tables.find(t => t.name === selectedTable);
            const primaryKey = currentTable.primaryKey;
            const isCompositePK = Array.isArray(primaryKey);
            
            let whereClause;
            let whereValues;
            
            if (isCompositePK) {
                // Composite primary key
                whereClause = primaryKey.map(key => `${key} = ?`).join(' AND ');
                whereValues = primaryKey.map(key => row[key]);
            } else {
                // Single primary key
                whereClause = `${primaryKey} = ?`;
                whereValues = [row[primaryKey]];
            }
            
            const query = `DELETE FROM ${selectedTable} WHERE ${whereClause}`;
            db.db.run(query, whereValues);
            
            setMessage({ type: 'success', text: 'Запис успішно видалено!' });
            loadTableData(selectedTable);
            
            setTimeout(() => setMessage({ type: '', text: '' }), 3000);
        } catch (error) {
            console.error('Error deleting record:', error);
            setMessage({ type: 'error', text: 'Помилка видалення: ' + error.message });
        }
    };

    const renderInput = (column, value) => {
        // Check if this is a foreign key
        if (foreignKeys[column]) {
            return (
                <select
                    value={value || ''}
                    onChange={(e) => handleInputChange(column, e.target.value)}
                    className="edit-input"
                >
                    <option value="">Оберіть...</option>
                    {foreignKeys[column].map(item => (
                        <option key={item.id} value={item.id}>
                            {item.name || `${item.symbol} - ${item.name}`}
                        </option>
                    ))}
                </select>
            );
        }
        
        // Regular input
        return (
            <input
                type="text"
                value={value || ''}
                onChange={(e) => handleInputChange(column, e.target.value)}
                className="edit-input"
            />
        );
    };

    const downloadDatabase = () => {
        try {
            const data = db.exportDatabase();
            const blob = new Blob([data], { type: 'application/x-sqlite3' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `materials_db_${new Date().toISOString().slice(0, 10)}.db`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            setMessage({ type: 'success', text: 'База даних успішно завантажена!' });
            setTimeout(() => setMessage({ type: '', text: '' }), 3000);
        } catch (error) {
            console.error('Error downloading database:', error);
            setMessage({ type: 'error', text: 'Помилка завантаження: ' + error.message });
        }
    };

    const renderCellValue = (column, value) => {
        if (value === null || value === undefined) {
            return <span className="null-value">NULL</span>;
        }
        
        // If it's a foreign key, try to show the related name
        if (foreignKeys[column]) {
            const relatedItem = foreignKeys[column].find(item => item.id === value);
            if (relatedItem) {
                return relatedItem.name || `${relatedItem.symbol} - ${relatedItem.name}`;
            }
        }
        
        return String(value);
    };

    return (
        <div className="entity-manager">
            <div className="manager-header">
                <h2>Управління даними</h2>
                <p className="manager-description">
                    Додавання, редагування та видалення записів у базі даних
                </p>
            </div>

            {message.text && (
                <div className={`message ${message.type}`}>
                    {message.text}
                </div>
            )}

            <div className="table-selector">
                <label>Оберіть таблицю:</label>
                <div className="table-buttons">
                    {tables.map(table => (
                        <button
                            key={table.name}
                            className={`table-button ${selectedTable === table.name ? 'active' : ''}`}
                            onClick={() => handleTableChange(table.name)}
                        >
                            {table.label}
                        </button>
                    ))}
                </div>
            </div>

            <div className="actions-bar">
                <div className="actions-left">
                    <button onClick={startAdd} className="add-button">
                        ➕ Додати новий запис
                    </button>
                    <button onClick={downloadDatabase} className="download-db-button">
                        💾 Завантажити БД
                    </button>
                </div>
                <span className="record-count">
                    📊 Записів: <strong>{tableData.length}</strong>
                </span>
            </div>

            {(isAdding || editingRow) && (
                <div className="edit-form">
                    <h3>{isAdding ? 'Додати новий запис' : 'Редагувати запис'}</h3>
                    <div className="form-grid">
                        {columns.filter(col => col !== 'id').map(column => (
                            <div key={column} className="form-field">
                                <label>{column}</label>
                                {renderInput(column, formData[column])}
                            </div>
                        ))}
                    </div>
                    <div className="form-actions">
                        <button onClick={saveRecord} className="save-button">
                            💾 Зберегти
                        </button>
                        <button onClick={cancelEdit} className="cancel-button">
                            ✕ Скасувати
                        </button>
                    </div>
                </div>
            )}

            <div className="table-container">
                {tableData.length > 0 ? (
                    <table className="data-table">
                        <thead>
                            <tr>
                                {columns.map(col => (
                                    <th key={col}>{col}</th>
                                ))}
                                <th>Дії</th>
                            </tr>
                        </thead>
                        <tbody>
                            {tableData.map((row, index) => (
                                <tr key={index}>
                                    {columns.map(col => (
                                        <td key={col}>
                                            {renderCellValue(col, row[col])}
                                        </td>
                                    ))}
                                    <td>
                                        <div className="action-buttons">
                                            <button
                                                onClick={() => startEdit(row)}
                                                className="edit-btn"
                                                title="Редагувати"
                                            >
                                                ✏️
                                            </button>
                                            <button
                                                onClick={() => deleteRecord(row)}
                                                className="delete-btn"
                                                title="Видалити"
                                            >
                                                🗑️
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                ) : (
                    <div className="no-data">
                        <p>📭 Немає даних у цій таблиці</p>
                    </div>
                )}
            </div>
        </div>
    );
}

export default EntityManager;
